/**
 * Bundle discount detection.
 *
 * The site markets BUNDLES (defined in ./bundles) as duo/trio protocol packs
 * with a fixed bundle price that is lower than the sum of retail prices.
 * When a customer adds those SKUs to the cart individually, we detect the
 * combination here and apply the bundle savings as a real discount — both
 * in the client cart UI and server-side at Razorpay order creation, so the
 * amount charged matches what the customer sees.
 *
 * Pure, isomorphic — no server imports, safe to use in the browser bundle.
 */
import { resolveRules, ruleRate, type BundleRule } from "./bundles";
import { getPackPricingTable, inferSkuHandle, type SkuHandle } from "./checkout-pricing";
import {
  computeInauguralDiscounts,
  getActiveCampaigns,
  type InauguralApplied,
  type PromoCampaign,
} from "./promo/inaugural";

export type BundleLine = {
  /** Stable per-line key so we can decrement remaining qty. */
  key: string;
  handle?: string;
  title?: string;
  unitPricePaise: number;
  quantity: number;
};

export type BundleApplied = {
  slug: string;
  title: string;
  count: number;
  savingsPaise: number;
};

export type BundleDiscountResult = {
  discountPaise: number;
  applied: BundleApplied[];
};

function haystack(line: BundleLine): string {
  return `${line.title ?? ""} ${line.handle ?? ""}`.toLowerCase();
}

/** Normalise "Liver Biome Plus" / "liver_biome+plus" → "liver biome plus". */
function normalise(s: string): string {
  return s.toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();
}

/**
 * A line satisfies a rule handle by exact handle match, by normalised
 * substring (so a display title like "Liver Biome Plus" matches the handle
 * "liver-biome-plus"), or by SKU inference from the title. Server checkout
 * paths often only know the product title, so title-only matching must work
 * or the cart shows bundle savings the payment page never applies.
 */
function matchesHandle(line: BundleLine, handle: string): boolean {
  const target = normalise(handle);
  if (line.handle && normalise(line.handle) === target) return true;
  if (normalise(haystack(line)).includes(target)) return true;
  const inferred = inferHandle(line);
  return inferred ? normalise(inferred) === target : false;
}

/** Rules with more items first (trio before duo) so bigger savings win. */
function orderedRules(rules?: BundleRule[] | null): BundleRule[] {
  return [...resolveRules(rules)].sort(
    (a, b) => b.matchHandles.length - a.matchHandles.length,
  );
}

export type TieredApplied = {
  handle: SkuHandle;
  title: string;
  packQty: 2 | 3;
  count: number;
  savingsPaise: number;
};

export type CartDiscountResult = {
  /** Total discount in paise = inaugural campaign + bundle + tiered pack. */
  discountPaise: number;
  bundleDiscountPaise: number;
  tieredDiscountPaise: number;
  /** Inaugural 90-Day Protocol campaign savings (admin-managed). */
  inauguralDiscountPaise: number;
  bundlesApplied: BundleApplied[];
  tieredApplied: TieredApplied[];
  inauguralApplied: InauguralApplied[];
};

function inferHandle(line: BundleLine): SkuHandle | null {
  if (line.handle && getPackPricingTable(line.handle)) return line.handle as SkuHandle;
  return inferSkuHandle({ handle: line.handle, title: line.title });
}

export function computeBundleDiscount(
  input: BundleLine[],
  rules?: BundleRule[] | null,
): BundleDiscountResult {
  const r = computeCartDiscounts(input, rules);
  return { discountPaise: r.bundleDiscountPaise, applied: r.bundlesApplied };
}

/**
 * Inaugural campaign first (it replaces the ladder price for its units), then
 * bundle detection (larger match groups first, greedy), then per-line tiered
 * pack discount on the REMAINING quantity of each SKU so no unit is ever
 * discounted twice. Tiered pricing comes from PACK_PRICING and favours the
 * 3-pack (largest per-unit saving), then 2-pack.
 *
 * `rules` come from `public.bundle_rules` and `campaigns` from
 * `public.promo_campaigns` (both admin-managed). When omitted, seeded bundle
 * defaults and the hydrated campaign registry are used so pricing never breaks.
 */
export function computeCartDiscounts(
  input: BundleLine[],
  rules?: BundleRule[] | null,
  campaigns?: PromoCampaign[] | null,
  now: Date = new Date(),
): CartDiscountResult {
  const remaining = new Map<string, number>();
  for (const l of input) remaining.set(l.key, l.quantity);

  // 1. Inaugural protocol campaign — consumes its units up front.
  const inaugural = computeInauguralDiscounts(
    input.map((l) => ({
      key: l.key,
      handle: l.handle,
      title: l.title,
      unitPricePaise: l.unitPricePaise,
      quantity: l.quantity,
    })),
    campaigns ?? getActiveCampaigns(),
    now,
  );
  for (const [key, qty] of Object.entries(inaugural.consumedByKey)) {
    remaining.set(key, Math.max(0, (remaining.get(key) ?? 0) - qty));
  }

  const bundlesApplied: BundleApplied[] = [];
  let bundleTotal = 0;

  for (const bundle of orderedRules(rules)) {
    const qtyPerItem = bundle.quantityPerItem || 1;
    const rate = ruleRate(bundle);
    const hasFixed = !!bundle.fixedPricePaise && bundle.fixedPricePaise > 0;
    if (!hasFixed && rate <= 0) continue;
    if (bundle.matchHandles.length < 2) continue;

    while (true) {
      const chosen: BundleLine[] = [];
      const takeFrom = new Map<string, number>();
      let ok = true;
      for (const handle of bundle.matchHandles) {
        const candidate = input.find(
          (l) =>
            matchesHandle(l, handle) &&
            (remaining.get(l.key) ?? 0) - (takeFrom.get(l.key) ?? 0) >= qtyPerItem,
        );
        if (!candidate) {
          ok = false;
          break;
        }
        chosen.push(candidate);
        takeFrom.set(candidate.key, (takeFrom.get(candidate.key) ?? 0) + qtyPerItem);
      }
      if (!ok) break;

      const bundleRetailPaise = chosen.reduce(
        (s, c) => s + c.unitPricePaise * qtyPerItem,
        0,
      );
      const targetPaise = hasFixed
        ? bundle.fixedPricePaise!
        : Math.round(bundleRetailPaise * (1 - rate));
      const savings = Math.max(0, bundleRetailPaise - targetPaise);
      if (savings <= 0) break;


      bundleTotal += savings;
      for (const [key, qty] of takeFrom) {
        remaining.set(key, (remaining.get(key) ?? 0) - qty);
      }

      const existing = bundlesApplied.find((a) => a.slug === bundle.slug);
      if (existing) {
        existing.count += 1;
        existing.savingsPaise += savings;
      } else {
        bundlesApplied.push({
          slug: bundle.slug,
          title: bundle.title,
          count: 1,
          savingsPaise: savings,
        });
      }
    }
  }

  // Tiered pack discount on remaining qty per SKU. Greedy 3-pack → 2-pack.
  const tieredApplied: TieredApplied[] = [];
  let tieredTotal = 0;

  for (const line of input) {
    const handle = inferHandle(line);
    if (!handle) continue;
    const tiers = getPackPricingTable(handle);
    if (!tiers) continue;
    let qty = remaining.get(line.key) ?? 0;
    if (qty < 2) continue;

    const retail = line.unitPricePaise;
    // Only apply if the catalog's 1-pack price matches the line's retail unit
    // price (guards against unexpected variant pricing that would silently
    // over- or under-discount the customer).
    const catalog1 = Math.round(tiers[1].price * 100);
    if (Math.abs(catalog1 - retail) > 100) continue; // >₹1 mismatch: skip

    for (const pack of [3, 2] as const) {
      const packPaise = Math.round(tiers[pack].price * 100);
      const savingsPerChunk = pack * retail - packPaise;
      if (savingsPerChunk <= 0) continue;
      while (qty >= pack) {
        qty -= pack;
        tieredTotal += savingsPerChunk;
        const existing = tieredApplied.find(
          (a) => a.handle === handle && a.packQty === pack,
        );
        if (existing) {
          existing.count += 1;
          existing.savingsPaise += savingsPerChunk;
        } else {
          tieredApplied.push({
            handle,
            title: line.title ?? handle,
            packQty: pack,
            count: 1,
            savingsPaise: savingsPerChunk,
          });
        }
      }
    }
    remaining.set(line.key, qty);
  }

  return {
    discountPaise: inaugural.discountPaise + bundleTotal + tieredTotal,
    bundleDiscountPaise: bundleTotal,
    tieredDiscountPaise: tieredTotal,
    inauguralDiscountPaise: inaugural.discountPaise,
    bundlesApplied,
    tieredApplied,
    inauguralApplied: inaugural.applied,
  };
}

/** Extract the product handle from a canonical /products/<handle> URL. */
export function handleFromProductUrl(url?: string | null): string | undefined {
  if (!url) return undefined;
  const m = /\/products\/([^/?#]+)/.exec(url);
  return m?.[1] ? decodeURIComponent(m[1]) : undefined;
}
